import cn from "classnames"
import { Fragment, useState } from "react"

import { Button, Dropdown, Flex, Popover, Space, Text } from "@/atoms"
import { NO_INTEGRATION_CREATE_BUTTON_KEY } from "@/const/ticketing.constants"
import { isTicketingSystem } from "@/domain/ticketing"
import { EllipsisOutlined, LinkOutlined, TagFilled, TagOutlined } from "@/icons"
import { ExternalLinkIcon } from "@/molecules/ExternalLinkIcon"
import {
  TicketingSystemLogo,
  TicketingSystemOption
} from "@/molecules/TicketingSystemLogo"
import { LinkTicketModal } from "./LinkTicketModal"

import styles from "./CreateTicketButton.module.scss"

import type { TicketingSystem } from "@/domain/ticketing"
import type { CreateTicketButtonProps } from "."

const DEFAULT_SYSTEMS: TicketingSystem[] = ["jira"]

const getLabelKeys = (system: TicketingSystem) =>
  system === "jira"
    ? {
        create: "jiraIssueModal.createButton",
        open: "jiraIssueModal.openNewJiraIssue",
        link: "jiraIssueModal.linkExistingJiraIssue"
      }
    : {
        create: `ticketing.createButton.${system}`,
        open: `ticketing.openNew.${system}`,
        link: `ticketing.linkExisting.${system}`
      }

export const CreateTicketButton = (props: CreateTicketButtonProps) => {
  const {
    isLoading,
    hasIntegration,
    t,
    canLink,
    issueId,
    sizeBtn = "xs",
    systems = DEFAULT_SYSTEMS
  } = props
  const disabled = props.disabled || !hasIntegration
  const Wrap = isLoading || hasIntegration ? Fragment : Popover
  const propsWrap =
    Wrap === Popover
      ? {
          content: (
            <span className={styles.contentTooltip}>
              <ExternalLinkIcon
                text={t("integrations.noIntegrationTicketSystem")}
                to="/integrations"
              />
            </span>
          )
        }
      : {}
  const system = systems[0] ?? "jira"
  const isMultiple = hasIntegration && systems.length > 1
  const labelKeys = getLabelKeys(system)
  const renderIcon = (className?: string) => {
    if (!hasIntegration) return <TagFilled className={className} />
    if (isMultiple) return <TagOutlined className={className} />

    return (
      <TicketingSystemLogo
        system={system}
        outlined={props.outlined}
        className={className}
      />
    )
  }
  const buttonClassName = cn(
    disabled && styles.disabled,
    !hasIntegration && styles.noIntegrationTicketSystem,
    props.className
  )
  const getButtonText = () => {
    if (!hasIntegration) return t(NO_INTEGRATION_CREATE_BUTTON_KEY)
    if (isMultiple) return t("ticketing.createButton.multiple")

    return t(labelKeys.create)
  }
  const [linkSystem, setLinkSystem] = useState<TicketingSystem | null>(null)
  const enabledLink = canLink && props.issueId

  const button = (
    <Button
      id={props.id}
      className={buttonClassName}
      disabled={disabled}
      loading={isLoading}
      size={sizeBtn}
      type={props.typeBtn}
      icon={renderIcon()}
      onClick={isMultiple ? undefined : () => props.onClick(system)}
    >
      {getButtonText()}
    </Button>
  )

  const linkItems = isMultiple
    ? systems.map((option) => ({
        key: option,
        label: (
          <TicketingSystemOption
            system={option}
            label={t(getLabelKeys(option).link)}
          />
        )
      }))
    : [
        {
          key: "open",
          label: (
            <Flex gap={8} align="center">
              {renderIcon("sm-text-ks")}
              <Text>{t(labelKeys.open)}</Text>
            </Flex>
          )
        },
        {
          key: "link",
          label: (
            <Flex gap={8} align="center">
              <LinkOutlined className="sm-text-ks" />
              <Text>{t(labelKeys.link)}</Text>
            </Flex>
          )
        }
      ]

  return (
    <>
      <Wrap {...propsWrap}>
        <Space.Compact>
          {isMultiple ? (
            <Dropdown
              trigger={["click"]}
              placement="bottom"
              disabled={disabled || isLoading}
              menu={{
                items: systems.map((option) => ({
                  key: option,
                  label: <TicketingSystemOption system={option} />
                })),
                onClick: ({ key }) => {
                  if (isTicketingSystem(key)) props.onClick(key)
                }
              }}
            >
              {button}
            </Dropdown>
          ) : (
            button
          )}
          {enabledLink && (
            <Dropdown
              trigger={["hover"]}
              placement="bottom"
              disabled={disabled || isLoading}
              menu={{
                items: linkItems,
                onClick: ({ key }) => {
                  if (key === "open") props.onClick(system)
                  if (key === "link") setLinkSystem(system)
                  if (isTicketingSystem(key)) setLinkSystem(key)
                }
              }}
            >
              <Button
                disabled={disabled || isLoading}
                size={sizeBtn}
                type={props.typeBtn === "primary" ? "primary" : "default"}
                icon={<EllipsisOutlined />}
              />
            </Dropdown>
          )}
        </Space.Compact>
        {props.below}
      </Wrap>
      {enabledLink && issueId && (
        <LinkTicketModal
          open={!!linkSystem}
          system={linkSystem ?? system}
          issueId={issueId}
          onCancel={() => setLinkSystem(null)}
        />
      )}
    </>
  )
}
